import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { ActionIcon, Modal } from "@mantine/core"
import { useDebouncedValue, useDisclosure, useHotkeys, useMediaQuery, useReducedMotion } from "@mantine/hooks"
import { IconSparkles } from "@tabler/icons-react"
import { useNavigate, useRouterState } from "@tanstack/react-router"
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyQuery } from "../../hooks/useCompanyQuery"
import { fetchMeetingsPage, searchMeetings } from "../../lib/api"
import { extractErrorMessage } from "../../lib/api/errors"
import { queryKeys } from "../../lib/query-keys"
import { useProduct } from "../../lib/tenant"
import type { ISearchHit } from "../../lib/types/search"
import { AskOverlayView } from "./AskOverlayView"
import { PALETTE_HIT_LIMIT, PALETTE_MIN_QUERY } from "./palette"
import { labelStarters, titlesForStarters } from "./starter-copy"
import { buildStarterQuestions } from "./starters"

interface IAskOverlayContext {
  /** Summon the search-and-ask palette. */
  open: () => void
}

const AskOverlayContext = createContext<IAskOverlayContext | null>(null)

/** Summon the palette from anywhere under {@link AskOverlayProvider}. */
export function useAskOverlay(): IAskOverlayContext {
  const ctx = useContext(AskOverlayContext)
  if (!ctx) throw new Error("useAskOverlay must be used within an AskOverlayProvider")
  return ctx
}

/**
 * The header sparkle. Opens the same palette as ⌘K. In Lite the header does
 * not render this (the layout hides Ask); ⌘K still opens search.
 */
export function AskTriggerButton(): React.JSX.Element {
  const { i18n } = useLingui()
  const { open } = useAskOverlay()
  const label = i18n._(msg`Search or ask (⌘K)`)
  return (
    <ActionIcon
      variant="subtle"
      size="lg"
      onClick={open}
      aria-label={label}
      title={label}
      data-testid="ask-overlay-trigger"
    >
      <IconSparkles size={18} />
    </ActionIcon>
  )
}

/**
 * Mounts the palette once for the authed app and wires ⌘K.
 *
 * `enabled` is the AI gate — the layout passes false in Lite, where Ask stays
 * hidden (ADR 0045). The palette still opens for a Lite user who has a
 * company, as search only: no Ask row and no starter questions.
 */
export function AskOverlayProvider({
  enabled = true,
  children,
}: {
  enabled?: boolean
  children: React.ReactNode
}): React.JSX.Element {
  const { user } = useAuth()
  const isLite = user?.experienceMode === "lite"
  const hasOrg = Boolean(user?.activeOrganizationId)
  const paletteEnabled = enabled || (isLite && hasOrg)
  const [opened, { open, close, toggle }] = useDisclosure(false)
  // Empty tagsToIgnore so ⌘K fires even from inputs.
  useHotkeys(paletteEnabled ? [["mod+K", () => toggle()]] : [], [])
  const value = useMemo<IAskOverlayContext>(() => ({ open }), [open])

  return (
    <AskOverlayContext.Provider value={value}>
      {children}
      {paletteEnabled ? <AskOverlayHost opened={opened} onClose={close} aiEnabled={enabled} /> : null}
    </AskOverlayContext.Provider>
  )
}

function AskOverlayHost({
  opened,
  onClose,
  aiEnabled,
}: {
  opened: boolean
  onClose: () => void
  aiEnabled: boolean
}): React.JSX.Element {
  const { i18n } = useLingui()
  const product = useProduct()
  const navigate = useNavigate()
  const isMobile = useMediaQuery("(max-width: 48em)") ?? false
  const reducedMotion = useReducedMotion()
  const [query, setQuery] = useState("")

  useEffect(() => {
    if (!opened) return
    setQuery("")
  }, [opened])

  const href = useRouterState({ select: (state) => state.location.href })
  const prevHref = useRef(href)
  useEffect(() => {
    if (href !== prevHref.current) {
      prevHref.current = href
      if (opened) onClose()
    }
  }, [href, opened, onClose])

  const trimmed = query.trim()
  const [debounced] = useDebouncedValue(trimmed, 200)
  const settled = debounced === trimmed
  const searchQuery = useCompanyQuery({
    key: (orgId) =>
      [
        ...queryKeys.search(orgId, debounced, undefined, undefined, undefined, undefined),
        "palette",
        PALETTE_HIT_LIMIT,
      ] as const,
    queryFn: (client) => searchMeetings(client, { q: debounced, limit: PALETTE_HIT_LIMIT }),
    enabled: opened && debounced.length >= PALETTE_MIN_QUERY,
    retry: false,
  })

  const payload = settled ? searchQuery.data : undefined
  const warming = payload?.status === "warming" && payload.hits.length === 0
  const hits = payload && !warming ? payload.hits.slice(0, PALETTE_HIT_LIMIT) : []
  const searching =
    trimmed.length >= PALETTE_MIN_QUERY && (!settled || searchQuery.isFetching || searchQuery.isLoading || warming)
  const searchError = settled && searchQuery.error ? extractErrorMessage(searchQuery.error) : null

  const meetingsQuery = useCompanyQuery({
    key: (orgId) => ["palette-recent-meetings", orgId] as const,
    queryFn: (client) => fetchMeetingsPage(client, { limit: 4 }),
    enabled: opened && aiEnabled,
    staleTime: 60_000,
  })

  const starters = useMemo(() => {
    if (!aiEnabled) return []
    const titles = titlesForStarters(meetingsQuery.data?.items ?? [], i18n)
    return labelStarters(buildStarterQuestions(titles, product), i18n)
  }, [aiEnabled, meetingsQuery.data, i18n, product])

  const onAsk = useCallback(
    (question: string): void => {
      onClose()
      void navigate({ to: "/ask", search: { q: question } })
    },
    [navigate, onClose],
  )

  const onOpenHit = useCallback(
    (hit: ISearchHit): void => {
      onClose()
      const seek = hit.seekStart
      void navigate({
        to: "/meetings/$id",
        params: { id: hit.meetingId },
        search: seek != null ? { t: Math.floor(seek) } : {},
      })
    },
    [navigate, onClose],
  )

  const onSeeAll = useCallback(
    (q: string): void => {
      onClose()
      void navigate({ to: "/search", search: { q } })
    },
    [navigate, onClose],
  )

  const title = aiEnabled ? i18n._(msg`Search or ask`) : i18n._(msg`Search`)

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size={640}
      centered={false}
      yOffset={isMobile ? 0 : "9dvh"}
      radius={isMobile ? 0 : "lg"}
      fullScreen={isMobile}
      transitionProps={{
        transition: isMobile ? "slide-up" : "pop",
        duration: reducedMotion ? 0 : isMobile ? 260 : 200,
      }}
    >
      <AskOverlayView
        query={query}
        onQueryChange={setQuery}
        aiEnabled={aiEnabled}
        hits={hits}
        searching={searching}
        searchError={searchError}
        starters={starters}
        onAsk={onAsk}
        onStarter={onAsk}
        onOpenHit={onOpenHit}
        onSeeAll={onSeeAll}
        onClose={onClose}
      />
    </Modal>
  )
}
